/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, message, Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
    DropDownComponent,
    TableComponent,
    CalendarComponent,
    RefreshTimestamp
} from "../../CommonComponents";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { getHubList, getHubCPList } from "../../../../services/api/hub";
import { ReportsService } from "../../../../services/api/reports";
import { downloadWrongSortingReport } from "../../../../utils/downloadUtils";
import { useRequestCancellation } from "../../../../hooks/useRequestCancellation";
import { SERVICE_TYPE_OPTIONS, BOOKING_TYPE_OPTIONS } from "../../../../constants/reportConstants";
import constants from "../../../../constants/constants";

const { ROLES } = constants;
const { HUB_ADMIN } = ROLES;

const WrongSortingReport = () => {
    // Global request cancellation hook
    const { createNewRequest, isLatestRequest } = useRequestCancellation();

    const [loading, setLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [dateRange, setDateRange] = useState([
        moment().subtract(1, "days"),
        moment()
    ]);
    const [hubList, setHubList] = useState([]);
    const [cpList, setCpList] = useState([]);
    const [toCpList, setToCpList] = useState([]);
    const [selectedFromHubs, setSelectedFromHubs] = useState([]);
    const [selectedMisrouteHubs, setSelectedMisrouteHubs] = useState([]);
    const [selectedFromCenters, setSelectedFromCenters] = useState([]);
    const [selectedToHubs, setSelectedToHubs] = useState([]);
    const [selectedToCenters, setSelectedToCenters] = useState([]);
    const [selectedServiceTypes, setSelectedServiceTypes] = useState([]);
    const [selectedBookingTypes, setSelectedBookingTypes] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 50,
        total: 0,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
    });
    const [reportData, setReportData] = useState([]);
    const [headerHubList, setHeaderHubList] = useState([]);
    const [hubUser, setHubUser] = useState();
    const [loggedInHubId, setLoggedInHubId] = useState(null);
    const [lastsynced, setLastsynced] = useState(null);
    const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);
    const [activeHubId, setActiveHubId] = useState(JSON.parse(localStorage.getItem("active_hub") || "{}")?.hubId || "ALL_HUBS");
    const [hasSavedFilters, setHasSavedFilters] = useState(false);

    // Fetch hubs from header/localStorage
    useEffect(() => {
        try {
            const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
            const userHubs = userFullData?.userInfo?.hubDetails || [];
            if (userHubs?.length > 0) {
                const headerHubs = [
                    { label: "All", value: "ALL" },
                    ...userHubs.map(hub => ({
                        label: hub.hubName,
                        value: hub.hubId
                    }))
                ];
                setHeaderHubList(headerHubs);
            } else {
                // If no hubs assigned, show all available hubs but without "All" option
                setHeaderHubList([]);
            }
        } catch (e) {
            console.error("Error parsing user hubs for Wrong Sorting Report", e);
            setHeaderHubList([]);
        }
    }, []);

    const handleMultiSelectChange = (values, setter, options = []) => {
        if (values.includes("ALL")) {
            const isLatestAll = values[values.length - 1] === "ALL";
            if (isLatestAll) {
                // Select all actual values (excluding the "ALL" option itself)
                const allActualValues = options
                    .filter(opt => opt.value !== "ALL")
                    .map(opt => opt.value);
                setter(allActualValues);
            } else {
                // If it wasn't the latest click, just filter it out (though shouldn't happen with this logic)
                setter(values.filter(v => v !== "ALL"));
            }
        } else {
            setter(values);
        }
    };

    // Ref to track whether we already set the hub for the current login session
    const hubInitializedRef = useRef(false);


    // Combined initial setup useEffect
    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const hubs = await getHubList();
                setHubList(hubs);
            } catch (error) {
                console.error("Error loading hub list:", error);
                message.error("Failed to fetch hub list");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    // Sync with App Header hub changes
    useEffect(() => {
        const handleHubChanged = () => {
            const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
            const newActiveHubId = activeHub?.hubId;
            setActiveHubId(newActiveHubId || "ALL_HUBS");
            if (newActiveHubId && newActiveHubId !== "ALL_HUBS") {
                setSelectedMisrouteHubs([newActiveHubId]);
            } else {
                setSelectedMisrouteHubs(["ALL"]);
            }
        };

        window.addEventListener("hubChanged", handleHubChanged);
        return () => window.removeEventListener("hubChanged", handleHubChanged);
    }, []);

    // Set default hub based on logged-in user — always enforced for hub-restricted roles
    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
        const userid = JSON.parse(localStorage.getItem("userId") || "{}");
        const role = user?.roles?.[0];
        setHubUser(role);

        const activeHub = JSON.parse(localStorage.getItem("active_hub") || "{}");
        const currentActiveHubId = activeHub?.hubId || "ALL_HUBS";
        setLoggedInHubId(currentActiveHubId);

        if (!hubInitializedRef.current) {
            const userFullData = JSON.parse(localStorage.getItem("userId") || "{}");
            const userHubs = userFullData?.userInfo?.hubDetails || [];
            
            if (currentActiveHubId && currentActiveHubId !== "ALL_HUBS") {
                setSelectedMisrouteHubs([currentActiveHubId]);
            } else if (userHubs?.length === 0 || role === "ROLE_HOADMIN" || role === "ROLE_CENTRALOPERATOR") {
                setSelectedMisrouteHubs([]);
            } else {
                setSelectedMisrouteHubs(["ALL"]);
            }
            hubInitializedRef.current = true;
        }
    }, [headerHubList]); // Depend on headerHubList to ensure we have options available

    // Fetch CP list when From Hub or Misroute Hub is selected
    useEffect(() => {
        const fromHubIds = Array.isArray(selectedFromHubs) ? selectedFromHubs : (selectedFromHubs ? [selectedFromHubs] : []);
        const misrouteHubIds = Array.isArray(selectedMisrouteHubs) ? selectedMisrouteHubs : (selectedMisrouteHubs ? [selectedMisrouteHubs] : []);
        const combinedHubIds = [...new Set([...fromHubIds, ...misrouteHubIds])];

        if (combinedHubIds.length === 0) {
            setCpList([]);
            setSelectedFromCenters([]);
            return;
        }

        (async () => {
            try {
                const cpLists = await Promise.all(combinedHubIds.map(hubId => getHubCPList(hubId)));
                // Flatten and remove duplicates by a unique property (e.g., value)
                const uniqueCps = Array.from(
                    new Map(cpLists.flat().map(cp => [cp.value, cp])).values()
                );
                setCpList(uniqueCps);
            } catch (error) {
                console.error("Failed to fetch CP list", error);
                message.error("Failed to fetch CP list");
                setCpList([]);
            }
        })();
    }, [selectedFromHubs, selectedMisrouteHubs]);

    // Fetch CP list when To Hub is selected
    useEffect(() => {
        if (!selectedToHubs || selectedToHubs.length === 0) {
            setToCpList([]);
            setSelectedToCenters([]);
            return;
        }

        (async () => {
            try {
                const cpLists = await Promise.all(selectedToHubs.map(hubId => getHubCPList(hubId)));
                const allCps = [...new Set(cpLists.flat())];
                setToCpList(allCps);
            } catch (error) {
                console.error("Failed to fetch To CP list", error);
                message.error("Failed to fetch To CP list");
                setToCpList([]);
            }
        })();
    }, [selectedToHubs]);

    // Debounced effect for report fetching - prevents rapid API calls during filter changes
    useEffect(() => {
        // Wait for hubUser to be initialized
        if (!hubUser) {
            return;
        }

        // For HUBADMIN users, hub selection is mandatory
        if ((hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR") && selectedFromHubs.length === 0 && selectedMisrouteHubs.length === 0) {
            return; // Don't fetch if HUBADMIN has no hub selected
        }

        // Debounce the API call to prevent multiple rapid calls when resetting filters
        const timer = setTimeout(() => {
            if (dateRange && dateRange[0] && dateRange[1]) {
                fetchReport();
            } else {
                setReportData([]);
                setPagination(prev => ({
                    ...prev,
                    total: 0,
                    current: 1
                }));
            }
        }, 300);

        return () => clearTimeout(timer);
        // eslint-disable-next-line
    }, [hubUser, dateRange, selectedFromHubs, selectedMisrouteHubs, selectedFromCenters, selectedToHubs, selectedToCenters, selectedServiceTypes, selectedBookingTypes, pagination.current, pagination.pageSize]);

    // Debug useEffect to monitor hubList changes
    useEffect(() => {
        console.log("Hub list updated:", hubList);
    }, [hubList]);

    // Debug useEffect to monitor cpList changes
    useEffect(() => {
        console.log("CP list updated:", cpList);
    }, [cpList]);

    const fetchReport = async () => {
        setReportData([]);
        if (!dateRange || !dateRange[0] || !dateRange[1]) return;

        // 🔥 CREATE NEW REQUEST WITH GLOBAL HOOK
        const { signal, requestId } = createNewRequest("wrong-sorting-report");

        try {
            setLoading(true);

            // Resolve Misroute Hubs (selectedMisrouteHubs) for API logic
            const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
            const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
            const resolvedMisrouteHubs = selectedMisrouteHubs?.includes("ALL")
                ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
                : selectedMisrouteHubs;

            const params = {
                fromDate: dateRange[0].format("YYYY-MM-DD"),
                toDate: dateRange[1].format("YYYY-MM-DD"),
                fromHubIds: selectedFromHubs,
                misrouteHubIds: resolvedMisrouteHubs,
                fromCenterIds: selectedFromCenters,
                toHubIds: selectedToHubs,
                toCenterIds: selectedToCenters,
                serviceTypes: selectedServiceTypes,
                bookingTypes: selectedBookingTypes,
                page: pagination.current - 1, // Convert to 0-based
                size: pagination.pageSize
            };

            const result = await ReportsService.getWrongSortingReport(params, { signal });

            // 🔥 ONLY UPDATE IF THIS IS THE LATEST REQUEST
            if (isLatestRequest(requestId)) {
                console.log(`🔥 Request ${requestId} completed successfully - updating state`);
                setReportData(result.data || []);
                setLastsynced(result.lastRefreshedAt);
                setNextScheduledRefresh(result.nextScheduledRefresh);
                setPagination(prev => ({
                    ...prev,
                    total: result.total || 0
                }));
            }
        } catch (error) {
            // 🔥 DON'T UPDATE STATE IF REQUEST WAS CANCELLED
            if (error.name === "AbortError") {
                console.log("🔥 Request was cancelled at HTTP level - no state update");
                return;
            }

            // Only update if this is still the latest request
            if (isLatestRequest(requestId)) {
                console.error("Error fetching report:", error);
                message.error("Failed to fetch report data");
            }
        } finally {
            // 🔥 ONLY UPDATE LOADING IF THIS IS THE LATEST REQUEST
            if (isLatestRequest(requestId)) {
                setLoading(false);
            }
        }
    };

    const handleDownload = async () => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) {
            message.warning("Please select date range");
            return;
        }

        setDownloadLoading(true);
        try {
            // Resolve Misroute Hubs (selectedMisrouteHubs) for Download logic
            const isSpecialRole = hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR";
            const activeHubOptions = isSpecialRole ? hubList : (headerHubList?.length > 0 ? headerHubList : hubList);
            const resolvedMisrouteHubs = selectedMisrouteHubs?.includes("ALL")
                ? activeHubOptions.filter(opt => opt.value !== "ALL").map(opt => opt.value)
                : selectedMisrouteHubs;

            const selectedFromHubData = hubList.filter(hub => selectedFromHubs?.includes(hub.value));
            const selectedToHubData = hubList.filter(hub => selectedToHubs?.includes(hub.value));

            const downloadParams = {
                fromDate: dateRange[0].format("YYYY-MM-DD"),
                toDate: dateRange[1].format("YYYY-MM-DD"),
                ...(selectedFromHubData?.length > 0 && { fromHubIds: selectedFromHubData.map(hub => hub.value) }),
                ...(resolvedMisrouteHubs?.length > 0 && { misrouteHubIds: resolvedMisrouteHubs }),
                ...(selectedFromCenters?.length > 0 && { fromCenterIds: selectedFromCenters }),
                ...(selectedToHubData?.length > 0 && { toHubIds: selectedToHubData.map(hub => hub.value) }),
                ...(selectedToCenters?.length > 0 && { toCenterIds: selectedToCenters }),
                ...(selectedServiceTypes?.length > 0 && { serviceTypes: selectedServiceTypes }),
                ...(selectedBookingTypes?.length > 0 && { bookingTypes: selectedBookingTypes })
            };

            await downloadWrongSortingReport(downloadParams);
            message.success("Download started successfully");
        } catch (error) {
            console.error("Error downloading wrong sorting report:", error);
            message.error("Failed to download report");
        } finally {
            setDownloadLoading(false);
        }
    };

    const handleTableChange = (paginationInfo) => {
        setPagination(prev => ({
            ...prev,
            current: paginationInfo.current,
            pageSize: paginationInfo.pageSize
        }));
    };

    const columns = [
        {
            title: "Date",
            dataIndex: "date",
            key: "date",
            width: 120,
            render: (date) => moment(date).format("DD-MM-YYYY")
        },
        {
            title: "Shipment No",
            dataIndex: "shipmentNo",
            key: "shipmentNo",
            width: 150,
            render: (text, record) => (
                <span
                    style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => {
                        const url = `/shipment-tracker?awb=${text}`;
                        window.open(url, "_blank");
                    }}
                >
                    {text}
                </span>
            )
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 120
        },
        {
            title: "From Center",
            dataIndex: "fromCenterName",
            key: "fromCenterName",
            width: 150,
            render: (text) => text || "-"
        },
        {
            title: "From Hub",
            dataIndex: "fromHubName",
            key: "fromHubName",
            width: 150
        },

        {
            title: "Misroute/Reroute Hub",
            dataIndex: "misrouteHubName",
            key: "misrouteHubName",
            width: 150
        },
        {
            title: "To Center",
            dataIndex: "toCenterName",
            key: "toCenterName",
            width: 150,
            render: (text) => text || "-"
        },
        {
            title: "To Hub",
            dataIndex: "toHubName",
            key: "toHubName",
            width: 150
        },
        //"defaulterHubName": "PUNE KIWALE HUB"
        {
            title: "Defaulter Hub",
            dataIndex: "defaulterHubName",
            key: "defaulterHubName",
            width: 150
        },
        {
            title: "Doc Type",
            dataIndex: "docType",
            key: "docType",
            width: 100
        },
        {
            title: "Service Type",
            dataIndex: "serviceType",
            key: "serviceType",
            width: 120
        },
        {
            title: "Booking Type",
            dataIndex: "bookingType",
            key: "bookingType",
            sorter: true,
            width: 120
        },
        {
            title: "State",
            dataIndex: "state",
            key: "state",
            width: 150
        },

    ];

    return (
        <div className="report-container">
            <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
                <Heading title="Wrong Sorting Report">
                    <div style={{ paddingTop: "5px" }}>
                        <RefreshTimestamp
                            lastRefreshedAt={lastsynced}
                            nextScheduledRefresh={nextScheduledRefresh}
                        />
                    </div>
                </Heading>
            </div>
            <Card className="report-card">
                <Row gutter={[16, 16]} className="filters-section">
                    <Col xs={24} sm={12} md={4} lg={4}>
                        <div className="filter-label">Date Range</div>
                        <CalendarComponent
                            className="custom-calendar"
                            type="range"
                            value={dateRange}
                            onChange={(value) => {
                                setDateRange(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            format="YYYY-MM-DD"
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={4} lg={4}>
                        <div className="filter-label">From Hub</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="From Hub"
                            options={hubList.filter(hub => loggedInHubId ? String(hub.value) !== String(loggedInHubId) : true)}
                            onChange={(value) => {
                                const selectedValues = Array.isArray(value) ? value : (value ? [value] : []);
                                setSelectedFromHubs(selectedValues);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={hubUser !== "ROLE_HUBADMIN" ? selectedFromHubs : (selectedFromHubs?.[0] || null)}
                            mode={hubUser !== "ROLE_HUBADMIN" ? "multiple" : "default"}
                            allowClear
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={4} lg={4}>
                        <div className="filter-label">To Hub</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="To Hub"
                            options={hubList}
                            onChange={(value) => {
                                setSelectedToHubs(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedToHubs}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>

                    <Col xs={24} sm={12} md={4} lg={4}>
                        <div className="filter-label">Misroute Hub</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Misroute Hub"
                            options={(hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList)}
                            onChange={(val) => {
                                const baseOptions = (hubUser === "ROLE_HOADMIN" || hubUser === "ROLE_CENTRALOPERATOR") ? hubList.filter(o => o.value !== "ALL") : (headerHubList?.length > 0 ? headerHubList : hubList);
                                handleMultiSelectChange(val, setSelectedMisrouteHubs, baseOptions);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedMisrouteHubs}
                            allowClear
                            mode="multiple"
                            maxTagCount={1}
                            maxTagPlaceholder={(omittedValues) => `... +${omittedValues.length}`}
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={4} lg={4}>
                        <div className="filter-label">From Center</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="From Center"
                            options={cpList}
                            onChange={(value) => {
                                setSelectedFromCenters(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedFromCenters}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={4} lg={4}>
                        <div className="filter-label">To Center</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="To Center"
                            options={toCpList}
                            onChange={(value) => {
                                setSelectedToCenters(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedToCenters}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                            disabled={toCpList.length === 0}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={4} lg={4}>
                        <div className="filter-label">Service Types</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select Service Types"
                            options={SERVICE_TYPE_OPTIONS.filter(opt => opt.value !== "ALL")}
                            onChange={(value) => {
                                setSelectedServiceTypes(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedServiceTypes}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={4} lg={4}>
                        <div className="filter-label">Booking Types</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select Booking Types"
                            options={BOOKING_TYPE_OPTIONS.filter(opt => opt.value !== "ALL")}
                            onChange={(value) => {
                                setSelectedBookingTypes(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedBookingTypes}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={16} lg={16} style={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-end" }}>
                        <Button
                            type="primary"
                            onClick={handleDownload}
                            loading={downloadLoading}
                            icon={<DownloadOutlined />}
                            disabled={!dateRange || !dateRange[0] || !dateRange[1]}
                        >
                            Download Report
                        </Button>
                    </Col>
                </Row>

                <div className="table-section">
                    <TableComponent
                        columns={columns}
                        dataSource={reportData}
                        loading={loading}
                        pagination={pagination}
                        onChange={handleTableChange}
                        rowKey="shipmentNo"
                        scroll={{ x: 1500 }}
                    />
                </div>
            </Card>
        </div>
    );
};

export default WrongSortingReport;
